import { triggerTracking } from '@zoopla/tracking';
import { getPropertyDataForAnalytics } from '@zoopla/analytics/property-data';
import type { PropertyDataForAnalytics } from '@zoopla/analytics/property-data';
import {
    Maybe,
    Property,
    TrackedPropertyRelationship
} from './graphql.generated';
import { useDeleteTrackedPropertyMutation } from './queries/deleteTrackedPropertyMutation.generated';
import { useSaveTrackedPropertyMutation } from './queries/saveTrackedPropertyMutation.generated';
import { trackEvent } from '@zoopla/tracking';
import { logError } from '@zoopla/logger';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';

export type Status = 'tracked' | 'untracked' | 'pending';

const recordTrackOrUntrackProperty = (
    analyticsData: PropertyDataForAnalytics & Record<string, unknown>,
    isCurrentlyTracked: boolean,
    relationship?: Maybe<TrackedPropertyRelationship>
) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'property details',
        eventAction: isCurrentlyTracked ? 'remove' : 'save',
        eventLabel: 'save property',
        eventValue: analyticsData.uprn
            ? parseInt(analyticsData.uprn, 10)
            : undefined,
        taxonomy: {
            postcode: analyticsData?.postcode,
            price_estimate: analyticsData?.priceEstimate ?? undefined,
            property_address: analyticsData?.propertyAddress,
            num_beds: analyticsData?.numBeds ?? undefined,
            uprn: analyticsData?.uprn ?? undefined,
            listing_id: analyticsData?.listingId
        }
    });

    trackEvent(
        isCurrentlyTracked ? 'Untrack Property' : 'Track Property Success',
        {
            section: 'house-prices',
            page: '/house-prices/results/',
            brand: 'zoopla',
            postcode: analyticsData?.postcode,
            priceEstimate: analyticsData?.priceEstimate ?? 0,
            propertyAddress: analyticsData?.propertyAddress,
            numBeds: analyticsData?.numBeds ?? undefined,
            uprn: analyticsData?.uprn ?? undefined,
            propertyRelationship: relationship ?? undefined,
            ...analyticsData
        }
    );
};

export const useTrackProperty = () => {
    const [saveTrackedProperty] = useSaveTrackedPropertyMutation();

    const [deleteTrackedProperty] = useDeleteTrackedPropertyMutation();

    const [isAuthModalShowing, dismissAuthModal] = useAuthModal();

    const trackOrUntrackProperty = async (
        p: Property,
        consentId?: string,
        consentText?: string,
        extraTaxonomy?: Record<string, unknown>
    ): Promise<Status> => {
        try {
            const { uprn } = p;

            if (!uprn) {
                return p?.trackedProperty?.isTracked ? 'tracked' : 'untracked';
            }

            const isCurrentlyTracked = p?.trackedProperty?.isTracked ?? false;

            if (isCurrentlyTracked) {
                await deleteTrackedProperty({
                    variables: {
                        uprn
                    }
                });
            } else {
                await saveTrackedProperty({
                    variables: {
                        trackedProperty: {
                            uprn,
                            consentId,
                            consentText
                        }
                    }
                });
            }

            recordTrackOrUntrackProperty(
                {
                    ...getPropertyDataForAnalytics(p),
                    ...extraTaxonomy
                },
                isCurrentlyTracked,
                p?.trackedProperty?.relationship
            );

            return isCurrentlyTracked ? 'untracked' : 'tracked';
        } catch (err) {
            logError('House prices useTrackProperty error: ', err);
            return p?.trackedProperty?.isTracked ? 'tracked' : 'untracked';
        }
    };

    return {
        trackOrUntrackProperty,
        isAuthModalShowing,
        dismissAuthModal
    };
};
